import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton } from "../components/Primitives";
import { Save, RotateCcw, Eye, MessageSquare, BellRing, MessageSquarePlus, Plus, Trash2, Sparkles, ShieldCheck, DollarSign, Clock } from "lucide-react";
import { extractPlaceholders } from "../lib/darQuickPhrases";
import MetricsSnapshotCard from "../components/MetricsSnapshotCard";
import ChipListEditor from "../components/ChipListEditor";

// ─── Registry — the label + description shown next to each toggle. ─
const METRIC_META = {
  sites:          { label: "Sites under coverage",       desc: "Active site count vs. total sites in the client's account." },
  guard_hours:    { label: "Officer-hours delivered",      desc: "Sum of shift durations across the rolling window." },
  tour_scans:     { label: "Tour scans completed",       desc: "GPS + NFC checkpoint hits — patrol coverage proof." },
  dars:           { label: "Daily activity reports",     desc: "Count of DARs filed by officers on their sites." },
  incidents:      { label: "Incidents observed",         desc: "Total incidents logged (observation + report focus, not open/closed)." },
  maintenance:    { label: "Maintenance issues flagged", desc: "Proactive maintenance requests raised by officers." },
  attendance:     { label: "On-time attendance rate",    desc: "% of shifts where clock-in was within 10 minutes of scheduled start." },
  live_coverage:  { label: "Live coverage right now",    desc: "Officers currently clocked-in across the client's sites." },
  activity_total: { label: "Total activity events",      desc: "One combined 'we did X things for you' number." },
};

const COMMENT_META = {
  incidents:   { label: "Incidents",         desc: "Allow clients to comment on incident reports." },
  maintenance: { label: "Maintenance",       desc: "Allow clients to comment on maintenance requests." },
  dar:         { label: "DARs",              desc: "Allow clients to comment on daily activity reports." },
  tour_scans:  { label: "Tour scans",        desc: "Allow clients to comment on tour scan entries." },
};

const APPROVAL_META = {
  incident:    { label: "Incidents",         desc: "Which severity threshold triggers a required supervisor sign-off?", kind: "severity" },
  dar:         { label: "Daily activity reports (DAR)", desc: "When should DARs be routed for approval?", kind: "dar" },
  maintenance: { label: "Maintenance requests", desc: "Which severity triggers required supervisor sign-off?", kind: "severity" },
};

const SEVERITY_OPTIONS = [
  { value: "low",      label: "Low or above (all)" },
  { value: "medium",   label: "Medium or above" },
  { value: "high",     label: "High or above" },
  { value: "critical", label: "Critical only" },
];

const DAR_TRIGGER_OPTIONS = [
  { value: "always",             label: "Every DAR" },
  { value: "on_maintenance",     label: "Only when maintenance issues logged" },
  { value: "on_notable_events",  label: "Only when notable events logged" },
];

function Toggle({ enabled, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center transition-colors border ${
        enabled ? "bg-[#FF3B30] border-[#FF3B30]" : "bg-[#141414] border-[#27272A]"
      }`}
      aria-checked={enabled}
      role="switch"
    >
      <span
        className={`inline-block size-4 transform bg-white transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}


export default function ClientPortalSettings() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [ops, setOps] = useState(null);         // operational settings snapshot
  const [opsDraft, setOpsDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o] = await Promise.all([
        api.get("/agency/portal-settings"),
        api.get("/agency/operational-settings").catch(() => ({ data: null })),
      ]);
      setSettings(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data)));
      if (o?.data) {
        setOps(o.data);
        setOpsDraft(JSON.parse(JSON.stringify(o.data)));
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isDirty =
    JSON.stringify(settings) !== JSON.stringify(draft) ||
    JSON.stringify(ops) !== JSON.stringify(opsDraft);

  const setMetric = (k, v) => setDraft((d) => ({ ...d, metrics: { ...d.metrics, [k]: v } }));
  const setComment = (k, v) => setDraft((d) => ({ ...d, comments: { ...d.comments, [k]: v } }));

  const setEscalationHours = (severity, hours) => setOpsDraft((d) => ({
    ...d,
    escalation_hours: { ...(d?.escalation_hours || {}), [severity]: Number(hours) || 0 },
  }));
  const setPhrase = (idx, text) => setOpsDraft((d) => {
    const arr = [...(d?.dar_quick_phrases || [])];
    arr[idx] = text;
    return { ...d, dar_quick_phrases: arr };
  });
  const setApproval = (kind, patch) => setOpsDraft((d) => {
    const existing = (d?.report_approvals || {})[kind] || {};
    return {
      ...d,
      report_approvals: { ...(d?.report_approvals || {}), [kind]: { ...existing, ...patch } },
    };
  });
  const addPhrase = () => setOpsDraft((d) => ({
    ...d,
    dar_quick_phrases: [...(d?.dar_quick_phrases || []), ""],
  }));
  const removePhrase = (idx) => setOpsDraft((d) => ({
    ...d,
    dar_quick_phrases: (d?.dar_quick_phrases || []).filter((_, i) => i !== idx),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const jobs = [];
      if (JSON.stringify(settings) !== JSON.stringify(draft)) {
        jobs.push(api.put("/agency/portal-settings", draft).then((r) => {
          setSettings(r.data);
          setDraft(JSON.parse(JSON.stringify(r.data)));
        }));
      }
      if (opsDraft && JSON.stringify(ops) !== JSON.stringify(opsDraft)) {
        // Strip empty phrases before saving.
        const cleanList = (arr) => (arr || []).map((p) => (p || "").trim()).filter(Boolean);
        const payload = {
          escalation_hours:  opsDraft.escalation_hours,
          dar_quick_phrases: cleanList(opsDraft.dar_quick_phrases),
          ir_quick_titles:   cleanList(opsDraft.ir_quick_titles),
          ir_quick_phrases:  cleanList(opsDraft.ir_quick_phrases),
          mr_quick_titles:   cleanList(opsDraft.mr_quick_titles),
          mr_quick_phrases:  cleanList(opsDraft.mr_quick_phrases),
          incident_types:    cleanList(opsDraft.incident_types),
          default_bill_rate_per_hour: opsDraft.default_bill_rate_per_hour,
          overtime_threshold_hours:   opsDraft.overtime_threshold_hours,
          email_scan_log_limit:       opsDraft.email_scan_log_limit,
          email_photo_limit:          opsDraft.email_photo_limit,
          idle_timeout_minutes:       opsDraft.idle_timeout_minutes,
        };
        jobs.push(api.put("/agency/operational-settings", payload).then((r) => {
          setOps(r.data);
          setOpsDraft(JSON.parse(JSON.stringify(r.data)));
        }));
      }
      await Promise.all(jobs);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    setDraft(JSON.parse(JSON.stringify(settings)));
    if (ops) setOpsDraft(JSON.parse(JSON.stringify(ops)));
  };

  return (
    <div data-testid="client-portal-settings-page">
      <PageHeader
        title="Client Portal Visibility"
        subtitle="Choose what your clients see on their portal dashboard and which report tabs accept their comments. Applies to every client user in your agency."
        action={
          <div className="inline-flex items-center gap-2">
            {isDirty && (
              <GhostButton onClick={revert} disabled={saving} data-testid="portal-settings-revert">
                <RotateCcw className="size-3.5 mr-1" /> Revert
              </GhostButton>
            )}
            <GhostButton onClick={save} disabled={!isDirty || saving} data-testid="portal-settings-save">
              <Save className="size-3.5 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </GhostButton>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {loading || !draft ? (
          <div className="text-zinc-500 text-sm p-8 text-center">Loading…</div>
        ) : (
          <>
            {/* ─── Notification Console entry point ─── */}
            <a
              href="/settings/notifications"
              data-testid="notification-console-link"
              className="block border border-[#27272A] bg-[#0A0A0A] hover:bg-[#141414] transition-colors px-5 py-4"
            >
              <div className="flex items-center gap-3">
                <BellRing className="size-5 text-[#FF3B30]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-100">Notification Console</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    Master switch, quiet hours, recipient rosters, and per-stream configuration for every automated email.
                  </div>
                </div>
                <div className="text-zinc-500">›</div>
              </div>
            </a>

            {/* ─── Dashboard metrics ─── */}
            <div className="border border-[#27272A] bg-[#0A0A0A]">
              <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                <Eye className="size-4 text-zinc-400" />
                <div className="label-mute">Dashboard metrics</div>
              </div>
              <div className="divide-y divide-[#1c1c1e]" data-testid="portal-settings-metrics">
                {Object.entries(METRIC_META).map(([k, meta]) => (
                  <div key={k} className="px-5 py-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-100">{meta.label}</div>
                      <div className="text-xs text-zinc-500 mt-1">{meta.desc}</div>
                    </div>
                    <Toggle
                      enabled={!!draft.metrics[k]}
                      onChange={(v) => setMetric(k, v)}
                      testId={`portal-metric-toggle-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* ─── Client comments ─── */}
            <div className="border border-[#27272A] bg-[#0A0A0A]">
              <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                <MessageSquare className="size-4 text-zinc-400" />
                <div className="label-mute">Client comments on reports</div>
              </div>
              <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                When enabled, portal users can leave a comment on individual entries within that tab. When disabled the tab remains read-only.
              </div>
              <div className="divide-y divide-[#1c1c1e]" data-testid="portal-settings-comments">
                {Object.entries(COMMENT_META).map(([k, meta]) => (
                  <div key={k} className="px-5 py-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-100">{meta.label}</div>
                      <div className="text-xs text-zinc-500 mt-1">{meta.desc}</div>
                    </div>
                    <Toggle
                      enabled={!!draft.comments[k]}
                      onChange={(v) => setComment(k, v)}
                      testId={`portal-comment-toggle-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* ─── Report approval workflow ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-approvals-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <ShieldCheck className="size-4 text-zinc-400" />
                  <div className="label-mute">Report approval workflow</div>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  When ON, new reports of that kind land as <span className="font-mono text-amber-400">PENDING</span> and stay hidden from the client portal until a supervisor / Operations Manager approves them. <span className="font-mono text-red-400">REJECTED</span> reports never reach the client.
                </div>
                <div className="divide-y divide-[#1c1c1e]">
                  {Object.entries(APPROVAL_META).map(([k, meta]) => {
                    const rule = (opsDraft.report_approvals || {})[k] || {};
                    const enabled = !!rule.enabled;
                    return (
                      <div key={k} className="px-5 py-4" data-testid={`ops-approval-row-${k}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="font-medium text-zinc-100">Require approval — {meta.label}</div>
                            <div className="text-xs text-zinc-500 mt-1">{meta.desc}</div>
                          </div>
                          <Toggle
                            enabled={enabled}
                            onChange={(v) => setApproval(k, { enabled: v })}
                            testId={`ops-approval-toggle-${k}`}
                          />
                        </div>
                        {enabled && (
                          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`ops-approval-trigger-${k}`}>
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Trigger</span>
                            {meta.kind === "severity" ? (
                              <select
                                value={rule.min_severity || "low"}
                                onChange={(e) => setApproval(k, { min_severity: e.target.value })}
                                data-testid={`ops-approval-severity-${k}`}
                                className="bg-transparent border border-[#27272A] text-sm px-2 py-1 focus:outline-none focus:border-[#FF3B30]"
                              >
                                {SEVERITY_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            ) : (
                              <select
                                value={rule.when || "always"}
                                onChange={(e) => setApproval(k, { when: e.target.value })}
                                data-testid={`ops-approval-when-${k}`}
                                className="bg-transparent border border-[#27272A] text-sm px-2 py-1 focus:outline-none focus:border-[#FF3B30]"
                              >
                                {DAR_TRIGGER_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── Default bill rate (Metrics fallback) ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-bill-rate-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <DollarSign className="size-4 text-zinc-400" />
                  <div className="label-mute">Default bill rate</div>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  Fallback hourly bill-rate used by the <span className="text-zinc-300">Reports → Metrics</span> tab
                  to compute unrealized revenue. A site&apos;s own <span className="font-mono text-zinc-300">bill_rate_per_hour</span>
                  {" "}always wins when set — this default only applies to sites that don&apos;t have one. Ships at
                  <span className="font-mono text-zinc-300"> $28/hr</span>.
                </div>
                <div className="px-5 py-4 flex items-baseline gap-3">
                  <span className="text-lg font-mono text-zinc-500">$</span>
                  <input
                    type="number"
                    min="0.01"
                    max="10000"
                    step="0.01"
                    value={opsDraft.default_bill_rate_per_hour ?? ""}
                    onChange={(e) => setOpsDraft((d) => ({
                      ...d,
                      default_bill_rate_per_hour: e.target.value === "" ? null : Number(e.target.value),
                    }))}
                    data-testid="ops-default-bill-rate"
                    className="w-32 bg-transparent border border-[#27272A] px-2 py-1 text-2xl font-mono font-black text-[#FF3B30] outline-none focus:border-[#FF3B30]"
                  />
                  <span className="text-xs text-zinc-500 uppercase tracking-widest">/ hour</span>
                </div>
              </div>
            )}

            {/* ─── Weekly OT threshold (Roster Health widget) ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-ot-threshold-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <Clock className="size-4 text-zinc-400" />
                  <div className="label-mute">Weekly overtime threshold</div>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  Weekly hours after which the <span className="text-zinc-300">Reports → Metrics → Roster Health</span> widget flags a guard as
                  {" "}<span className="text-[#FF3B30] font-mono">OVER</span>. Sun→Sat US-payroll week. Ships at <span className="font-mono text-zinc-300">40h</span> (FLSA
                  standard) — bump to 44 for CA-style carve-outs or drop to enforce part-time caps.
                </div>
                <div className="px-5 py-4 flex items-baseline gap-3">
                  <input
                    type="number"
                    min="1"
                    max="168"
                    step="1"
                    value={opsDraft.overtime_threshold_hours ?? ""}
                    onChange={(e) => setOpsDraft((d) => ({
                      ...d,
                      overtime_threshold_hours: e.target.value === "" ? null : Number(e.target.value),
                    }))}
                    data-testid="ops-ot-threshold"
                    className="w-24 bg-transparent border border-[#27272A] px-2 py-1 text-2xl font-mono font-black text-[#FF3B30] outline-none focus:border-[#FF3B30]"
                  />
                  <span className="text-xs text-zinc-500 uppercase tracking-widest">hours / week</span>
                </div>
              </div>
            )}

            {/* ─── Idle-timeout auto-logout (iter294) ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-idle-timeout-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <Clock className="size-4 text-zinc-400" />
                  <div className="label-mute">Idle-timeout auto-logout</div>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  Sign users out after this much inactivity. <span className="text-zinc-300">Fullscreen ops-center TV routes are always exempt</span> —
                  a display pinned to <span className="font-mono text-zinc-300">/shifts/fullscreen</span> will never idle-kill itself.
                  Warn toast fires 60 s before logout so users can nudge the mouse to stay in.
                </div>
                <div className="px-5 py-4 flex flex-wrap items-center gap-2">
                  {[
                    { v: 30,   label: "30 min" },
                    { v: 60,   label: "1 hour" },
                    { v: 180,  label: "3 hours" },
                    { v: 480,  label: "8 hours" },
                    { v: 0,    label: "Never" },
                  ].map(({ v, label }) => {
                    const selected = (opsDraft.idle_timeout_minutes ?? 180) === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        data-testid={`ops-idle-timeout-${v}`}
                        onClick={() => setOpsDraft((d) => ({ ...d, idle_timeout_minutes: v }))}
                        className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border transition-colors ${
                          selected
                            ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]"
                            : "border-[#27272A] hover:border-[#3F3F46] text-zinc-400"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="px-5 pb-4 text-[10px] uppercase tracking-widest text-zinc-500">
                  Currently <span className="text-zinc-300 font-mono">{(opsDraft.idle_timeout_minutes ?? 180) === 0
                    ? "auto-logout disabled"
                    : `${opsDraft.idle_timeout_minutes ?? 180} min`}</span>.
                  Applies to every user in this agency.
                </div>
              </div>
            )}

            {/* ─── Email inline limits (iter236) ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-email-limits-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <BellRing className="size-4 text-zinc-400" />
                  <div className="label-mute">Report email content limits</div>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  How much detail to pack into each report notification email before falling back to a
                  &ldquo;+N more&rdquo; link to the portal. Defaults are agency-wide;
                  per-site overrides live under <span className="text-zinc-300">Sites &rarr; site detail</span>.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-x divide-[#1c1c1e]">
                  <div className="px-5 py-4">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Scan-log rows (Detailed DAR)</div>
                    <div className="flex items-baseline gap-2 mt-2">
                      <input
                        type="number"
                        min="1"
                        max="50"
                        step="1"
                        value={opsDraft.email_scan_log_limit ?? ""}
                        onChange={(e) => setOpsDraft((d) => ({
                          ...d,
                          email_scan_log_limit: e.target.value === "" ? null : Number(e.target.value),
                        }))}
                        data-testid="ops-email-scan-log-limit"
                        className="w-20 bg-transparent border border-[#27272A] px-2 py-1 text-xl font-mono font-black text-[#FF3B30] outline-none focus:border-[#FF3B30]"
                      />
                      <span className="text-xs text-zinc-500 uppercase tracking-widest">rows &middot; default 8, max 50</span>
                    </div>
                  </div>
                  <div className="px-5 py-4">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Photos per report</div>
                    <div className="flex items-baseline gap-2 mt-2">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        step="1"
                        value={opsDraft.email_photo_limit ?? ""}
                        onChange={(e) => setOpsDraft((d) => ({
                          ...d,
                          email_photo_limit: e.target.value === "" ? null : Number(e.target.value),
                        }))}
                        data-testid="ops-email-photo-limit"
                        className="w-20 bg-transparent border border-[#27272A] px-2 py-1 text-xl font-mono font-black text-[#FF3B30] outline-none focus:border-[#FF3B30]"
                      />
                      <span className="text-xs text-zinc-500 uppercase tracking-widest">photos &middot; default 12, max 20 (first 8 large, rest as thumbs)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Complaint escalation SLA ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-escalation-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <BellRing className="size-4 text-zinc-400" />
                  <div className="label-mute">Complaint escalation SLA</div>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  Open complaints past their SLA get auto-emailed to your Operations Manager + CSO. Set the hours per severity — leave at 0 to disable that tier.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-x divide-[#1c1c1e]">
                  {["urgent", "high", "normal", "low"].map((sev) => (
                    <div key={sev} className="px-5 py-4">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{sev}</div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <input
                          type="number"
                          min="1"
                          max="720"
                          value={opsDraft.escalation_hours?.[sev] ?? ""}
                          onChange={(e) => setEscalationHours(sev, e.target.value)}
                          data-testid={`ops-sla-${sev}`}
                          className="w-20 bg-transparent border border-[#27272A] px-2 py-1 text-lg font-mono font-black text-[#FF3B30] outline-none focus:border-[#FF3B30]"
                        />
                        <span className="text-xs text-zinc-500 uppercase tracking-widest">hours</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── DAR quick-phrase presets ─── */}
            {opsDraft && (
              <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="ops-phrases-section">
                <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
                  <MessageSquarePlus className="size-4 text-zinc-400" />
                  <div className="label-mute">DAR quick-phrase presets</div>
                  <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                    {opsDraft.dar_quick_phrases?.length || 0} / 12
                  </span>
                </div>
                <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
                  Officers see these as tappable chips above the DAR narrative field. Tapping appends the phrase to their existing text — great for common uneventful-shift descriptions. Keep them honest and short.
                  <div className="mt-2 flex items-start gap-1.5 text-[#0A84FF]">
                    <Sparkles className="size-3 mt-0.5 flex-shrink-0" />
                    <span><span className="font-mono">{"{slot_name}"}</span> — insert placeholders to prompt the  for a fill-in. e.g. <span className="font-mono text-zinc-300">Perimeter of &#123;site&#125; walked at &#123;time&#125;.</span></span>
                  </div>
                </div>
                <div className="divide-y divide-[#1c1c1e]">
                  {(opsDraft.dar_quick_phrases || []).map((phrase, idx) => {
                    const slots = extractPlaceholders(phrase || "");
                    return (
                    <div key={idx} className="px-5 py-3 flex items-start gap-3">
                      <div className="label-mute mt-3 w-8 flex-shrink-0 font-mono">#{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <textarea
                          rows={2}
                          value={phrase}
                          onChange={(e) => setPhrase(idx, e.target.value)}
                          data-testid={`ops-phrase-${idx}`}
                          placeholder="e.g. Officer completed all scheduled scans on time."
                          className="w-full bg-[#121212] border border-[#27272A] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#FF3B30]"
                        />
                        {slots.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                            <span className="text-[9px] uppercase tracking-widest text-[#0A84FF] font-bold">
                              <Sparkles className="size-2.5 inline mr-1" strokeWidth={2.5} />
                              Fill-ins:
                            </span>
                            {slots.map((s) => (
                              <span key={s} className="px-1.5 py-0.5 text-[10px] font-mono text-[#0A84FF] bg-[#0A84FF]/10 border border-[#0A84FF]/30">
                                {"{" + s + "}"}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removePhrase(idx)}
                        title="Remove"
                        data-testid={`ops-phrase-remove-${idx}`}
                        className="p-2 mt-1 text-zinc-500 hover:text-[#FF3B30] transition-colors"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                    );
                  })}
                </div>
                {(opsDraft.dar_quick_phrases?.length || 0) < 12 && (
                  <div className="px-5 py-3 border-t border-[#1c1c1e]">
                    <button
                      type="button"
                      onClick={addPhrase}
                      data-testid="ops-phrase-add"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors"
                    >
                      <Plus className="size-3.5" /> Add phrase
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ─── IR / MR chip catalogues ─── */}
            {opsDraft && (
              <>
                <ChipListEditor
                  testIdPrefix="ops-ir_quick_titles"
                  label="Incident Report title chips"
                  hint="One-tap set the IR title. Short, category-style — 'Suspicious person', 'Fire alarm', etc. Rendered as chips above the TITLE field on the mobile IR form."
                  values={opsDraft.ir_quick_titles || []}
                  onChange={(next) => setOpsDraft((d) => ({ ...d, ir_quick_titles: next }))}
                  rows={1}
                  placeholder="e.g. Suspicious person"
                />
                <ChipListEditor
                  testIdPrefix="ops-ir_quick_phrases"
                  label="Incident Report narrative chips"
                  hint="Append-style templates for the IR narrative field. Use {slot_name} to prompt  for fill-ins."
                  values={opsDraft.ir_quick_phrases || []}
                  onChange={(next) => setOpsDraft((d) => ({ ...d, ir_quick_phrases: next }))}
                  rows={2}
                  showSlots
                  placeholder="e.g. Observed an individual at {location} at ~{time}."
                />
                <ChipListEditor
                  testIdPrefix="ops-mr_quick_titles"
                  label="Maintenance title chips"
                  hint="One-tap set the MR title. Short, category-style — 'Broken lock', 'HVAC failure', etc."
                  values={opsDraft.mr_quick_titles || []}
                  onChange={(next) => setOpsDraft((d) => ({ ...d, mr_quick_titles: next }))}
                  rows={1}
                  placeholder="e.g. Broken lock / hardware"
                />
                <ChipListEditor
                  testIdPrefix="ops-mr_quick_phrases"
                  label="Maintenance description chips"
                  hint="Append-style templates for the MR description field. Same {slot} syntax as DAR / IR."
                  values={opsDraft.mr_quick_phrases || []}
                  onChange={(next) => setOpsDraft((d) => ({ ...d, mr_quick_phrases: next }))}
                  rows={2}
                  showSlots
                  placeholder="e.g. Discovered issue during patrol at {location}."
                />
                <ChipListEditor
                  testIdPrefix="ops-incident_types"
                  label="Incident type catalogue"
                  hint="The picker at the top of every IR — pick as many as apply to the incident. Retail / hospital / transit agencies all have different vocabularies; tailor this to your ops. Up to 30 entries."
                  values={opsDraft.incident_types || []}
                  onChange={(next) => setOpsDraft((d) => ({ ...d, incident_types: next }))}
                  rows={1}
                  cap={30}
                  placeholder="e.g. Trespassing"
                />
              </>
            )}

            {/* ─── Weekly Metrics Snapshot ─── */}
            <MetricsSnapshotCard />

            {/* ─── Preview link ─── */}
            <div className="border border-[#27272A] bg-[#0A0A0A] p-4 text-xs text-zinc-400 leading-relaxed">
              <div className="uppercase tracking-widest text-zinc-500 text-[10px] mb-2">Preview</div>
              Changes save instantly for every client-portal user in your agency. Toggle a metric off and it disappears from the client dashboard the next time they refresh — no notice sent to them.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
